// Страница детальной информации о студенте для администраторов: статистика прохождений и история упражнений.

import { useEffect, useState } from 'react';
import {
  Container,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Card,
  Row,
  Col,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import AppNavbar from '../../components/common/AppNavbar';
import {
  getAdminStudents,
  getAdminStudentHistory,
  forceCompleteStudentSessions,
} from '../../api/adminApi';

/** Инициалы из имени */
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/** Форматирование даты */
function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Status codes:
 *   0 — незавершено (in progress)
 *   1 — завершено
 *   2 — завершено принудительно
 */
function StatusBadge({ status }) {
  if (status === 1) {
    return (
      <Badge bg="success" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
        Завершено
      </Badge>
    );
  }
  if (status === 2) {
    return (
      <Badge bg="secondary" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
        Завершено адм.
      </Badge>
    );
  }
  // status === 0 or unknown
  return (
    <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
      Незавершено
    </Badge>
  );
}

export default function AdminStudentDetailPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState('');

  const [showConfirm, setShowConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    loadData();
  }, [studentId]);

  async function loadData() {
    setLoadingStudent(true);
    setLoadingHistory(true);
    setError('');

    // Load student info from the list (no single-student GET endpoint required)
    getAdminStudents()
      .then((res) => {
        const found = res.data.find((s) => String(s.student_id) === String(studentId));
        if (!found) {
          setError('Студент не найден');
        } else {
          setStudent(found);
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.detail ?? 'Не удалось загрузить данные студента');
      })
      .finally(() => setLoadingStudent(false));

    getAdminStudentHistory(studentId)
      .then((res) => {
        setHistory(res.data);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail ?? 'Не удалось загрузить историю');
      })
      .finally(() => setLoadingHistory(false));
  }

  const pendingCount = history.filter((h) => h.status === 0).length;

  const openConfirm = () => {
    setActionError('');
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (actionLoading) return;
    setShowConfirm(false);
    setActionError('');
  };

  const handleForceComplete = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      await forceCompleteStudentSessions(studentId);
      // Reload both student stats and history
      const [studentsRes, historyRes] = await Promise.all([
        getAdminStudents(),
        getAdminStudentHistory(studentId),
      ]);
      const found = studentsRes.data.find((s) => String(s.id) === String(studentId));
      if (found) setStudent(found);
      setHistory(historyRes.data);
      setShowConfirm(false);
    } catch (err) {
      setActionError(err?.response?.data?.detail ?? 'Произошла ошибка. Попробуйте ещё раз.');
    } finally {
      setActionLoading(false);
    }
  };

  const isLoading = loadingStudent || loadingHistory;

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── Кнопка назад ── */}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/admin/students')}
            className="mb-4"
            style={{ borderRadius: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}
          >
            ← Все студенты
          </Button>

          {/* ── Состояние загрузки / ошибки ── */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                Загрузка данных студента...
              </p>
            </div>
          ) : error ? (
            <Alert variant="danger" style={{ borderRadius: '0.875rem' }}>
              {error}
            </Alert>
          ) : (
            <>
              {/* ── Шапка студента ── */}
              <div
                className="mb-4 p-4"
                style={{
                  background: '#fff',
                  borderRadius: '0.875rem',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                }}
              >
                <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                  {/* Имя + email */}
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="ewl-avatar"
                      style={{
                        width: '3rem',
                        height: '3rem',
                        fontSize: '1.1rem',
                        opacity: student?.is_active ? 1 : 0.45,
                      }}
                    >
                      {getInitials(student?.name)}
                    </div>
                    <div>
                      <h5
                        className="fw-bold mb-1"
                        style={{ color: '#1e2a4a', letterSpacing: '-0.01em' }}
                      >
                        {student?.name}
                      </h5>
                      <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        {student?.email}
                      </p>
                    </div>
                  </div>

                  {/* Статус + действие */}
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge
                      bg={student?.is_active ? 'success' : 'secondary'}
                      style={{ fontSize: '0.78rem', fontWeight: 600 }}
                    >
                      {student?.is_active ? 'Активен' : 'Неактивен'}
                    </Badge>
                    {pendingCount > 0 && !isLoading && (
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={openConfirm}
                        style={{ borderRadius: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}
                      >
                        Завершить все незавершённые
                      </Button>
                    )}
                  </div>
                </div>

                {/* ── Статистика ── */}
                <Row className="mt-4 g-3">
                  <Col xs={12} sm={4}>
                    <div
                      className="text-center py-3"
                      style={{
                        background: 'var(--ewl-bg-body)',
                        borderRadius: '0.625rem',
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{ fontSize: '1.75rem', color: '#1e2a4a' }}
                      >
                        {student?.total_attempts ?? 0}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                        Попыток всего
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={4}>
                    <div
                      className="text-center py-3"
                      style={{
                        background: '#f0fdf4',
                        borderRadius: '0.625rem',
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{ fontSize: '1.75rem', color: '#16a34a' }}
                      >
                        {student?.completed_attempts ?? 0}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                        Завершено
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={4}>
                    <div
                      className="text-center py-3"
                      style={{
                        background: '#fffbeb',
                        borderRadius: '0.625rem',
                      }}
                    >
                      <div
                        className="fw-bold"
                        style={{ fontSize: '1.75rem', color: '#d97706' }}
                      >
                        {student?.pending_attempts ?? 0}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                        Незавершено
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* ── История ── */}
              <div
                className="d-flex justify-content-between align-items-center mb-3"
                style={{ borderBottom: '1px solid #e9ecef', paddingBottom: '0.75rem' }}
              >
                <h6 className="fw-semibold mb-0" style={{ color: '#1e2a4a' }}>
                  История упражнений
                </h6>
                {history.length > 0 && (
                  <span className="text-muted" style={{ fontSize: '0.8125rem' }}>
                    {history.length}{' '}
                    {history.length === 1
                      ? 'запись'
                      : history.length < 5
                      ? 'записи'
                      : 'записей'}
                  </span>
                )}
              </div>

              {history.length === 0 ? (
                <div
                  className="text-center py-5"
                  style={{
                    background: '#fff',
                    borderRadius: '0.875rem',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                  <h6 className="fw-semibold text-muted mb-1">История пуста</h6>
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Студент ещё не выполнял упражнений
                  </p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '3rem' }}>#</th>
                        <th>Упражнение</th>
                        <th style={{ width: '10rem' }}>Вариант</th>
                        <th style={{ width: '10rem' }}>Статус</th>
                        <th style={{ width: '6rem' }}>Ошибок</th>
                        <th style={{ width: '12rem' }}>Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row, idx) => (
                        <tr key={row.id ?? idx}>
                          <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                            {idx + 1}
                          </td>
                          <td>
                            <span className="fw-medium" style={{ color: '#1e2a4a' }}>
                              {row.exercise_name ?? `Упражнение #${row.exercise_id}`}
                            </span>
                          </td>
                          <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                            {row.variant_name ?? (row.variant_id ? `#${row.variant_id}` : '—')}
                          </td>
                          <td>
                            <StatusBadge status={row.status} />
                          </td>
                          <td>
                            {row.errors_count != null ? (
                              <Badge
                                bg={row.errors_count === 0 ? 'success' : 'danger'}
                                style={{ fontSize: '0.7rem', fontWeight: 600 }}
                              >
                                {row.errors_count}
                              </Badge>
                            ) : (
                              <span className="text-muted" style={{ fontSize: '0.875rem' }}>—</span>
                            )}
                          </td>
                          <td className="text-muted" style={{ fontSize: '0.8125rem' }}>
                            {formatDate(row.created_at ?? row.date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Container>
      </div>

      {/* ── Модальное окно подтверждения ── */}
      <Modal show={showConfirm} onHide={closeConfirm}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Завершить незавершённые сессии
            </Modal.Title>
            <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
              {student?.name}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {actionError && (
            <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              {actionError}
            </Alert>
          )}
          <p className="mb-0" style={{ fontSize: '0.9375rem' }}>
            Будут принудительно завершены{' '}
            <strong>
              {pendingCount}{' '}
              {pendingCount === 1 ? 'незавершённая сессия' : 'незавершённых сессий'}
            </strong>{' '}
            студента <strong>{student?.name}</strong>. Это действие нельзя отменить.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeConfirm} disabled={actionLoading}>
            Отмена
          </Button>
          <Button
            variant="warning"
            onClick={handleForceComplete}
            disabled={actionLoading}
            style={{ fontWeight: 500 }}
          >
            {actionLoading ? <Spinner size="sm" animation="border" /> : 'Завершить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
